/**
 * Product — Interactive layer (pix) behavior.
 *
 * Progressively enhances the native Horizon product-information section
 * from the invisible `.pix-layer` marker rendered by
 * sections/product-interactive.liquid:
 * - reveal: staggered entrance animation for details blocks and the gallery
 * - tilt: pointer-tracking 3D tilt + moving highlight on gallery media
 *   (desktop fine-pointer only)
 * - auto accent: derives --pix-accent from the product's first image using
 *   the shared dominant-color sampler; the saved accent paints first and
 *   stays as the fallback
 *
 * Initializes every `[data-ix-pix]` layer on load and again on
 * `shopify:section:load` for theme-editor re-renders.
 */
import { sampleDominantColor } from '@theme/ix-accent';

function initLayer(layer) {
  if (!layer || layer.dataset.pixInit) return;
  layer.dataset.pixInit = 'true';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const info = document.querySelector('.product-information');
  if (!info) return;

  /* ---- staggered entrance reveal ---- */
  if (layer.hasAttribute('data-reveal') && !reduceMotion && 'IntersectionObserver' in window) {
    const targets = [];
    const media = info.querySelector('.product-information__media');
    if (media) targets.push(media);
    const detailBlocks = info.querySelectorAll(
      '.product-details > .group-block > .group-block-content > *:not(.view-product-title)'
    );
    for (let i = 0; i < detailBlocks.length; i++) targets.push(detailBlocks[i]);

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('pix-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    targets.forEach((el, index) => {
      el.classList.add('pix-reveal');
      el.style.setProperty('--pix-i', index);
      io.observe(el);
    });
  }

  /* ---- pointer tilt + moving highlight on gallery media ---- */
  if (layer.hasAttribute('data-tilt') && !reduceMotion && finePointer) {
    let tiltRaf = null;
    let activeTilt = null;
    const tiltMax =
      parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('--pix-tilt-max')) || 4;

    const resetTilt = (el) => {
      if (!el) return;
      el.style.setProperty('--pix-rx', '0deg');
      el.style.setProperty('--pix-ry', '0deg');
      el.style.setProperty('--pix-shine', '0');
    };

    // Delegated so bindings survive Horizon's DOM morphs on variant change
    info.addEventListener('pointermove', (e) => {
      let el = e.target.closest && e.target.closest('.product-information__media .product-media-container');
      if (el && el.closest('dialog')) el = null; // never tilt inside the zoom dialog
      if (el !== activeTilt) {
        resetTilt(activeTilt);
        activeTilt = el;
      }
      if (!el || e.buttons) return;
      if (tiltRaf) return;
      const x = e.clientX;
      const y = e.clientY;
      tiltRaf = requestAnimationFrame(() => {
        tiltRaf = null;
        const r = el.getBoundingClientRect();
        if (!r.width || !r.height) return;
        const px = (x - r.left) / r.width;
        const py = (y - r.top) / r.height;
        el.style.setProperty('--pix-ry', ((px - 0.5) * 2 * tiltMax).toFixed(2) + 'deg');
        el.style.setProperty('--pix-rx', ((0.5 - py) * 2 * tiltMax).toFixed(2) + 'deg');
        el.style.setProperty('--pix-sx', (px * 100).toFixed(1) + '%');
        el.style.setProperty('--pix-sy', (py * 100).toFixed(1) + '%');
        el.style.setProperty('--pix-shine', '1');
      });
    });

    info.addEventListener(
      'pointerdown',
      () => {
        resetTilt(activeTilt);
        activeTilt = null;
      },
      true
    );

    info.addEventListener('pointerleave', () => {
      resetTilt(activeTilt);
      activeTilt = null;
    });
  }

  /* ---- accent derived from the product artwork ---- */
  if (layer.hasAttribute('data-auto-accent')) {
    const art = info.querySelector('.product-information__media .product-media-container img');
    if (art) {
      const derive = () => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const color = sampleDominantColor(img, 32);
          if (!color) return; // tainted canvas or too little signal: keep the fallback accent
          const r = Math.round(color.r);
          const g = Math.round(color.g);
          const b = Math.round(color.b);
          document.documentElement.style.setProperty('--pix-accent', 'rgb(' + r + ' ' + g + ' ' + b + ')');
        };
        img.src = art.currentSrc || art.src;
      };
      if (art.complete) derive();
      else art.addEventListener('load', derive, { once: true });
    }
  }
}

function initAll(scope) {
  scope.querySelectorAll('[data-ix-pix]').forEach(initLayer);
}

initAll(document);
document.addEventListener('shopify:section:load', (event) => initAll(event.target));
